import type { ApiError as ApiErrorShape, AuthResponse } from '@conversia/shared-types';
import { useAuthStore } from '@/store/auth.store';
import { getActiveWorkspaceId } from '@/store/workspace.store';
import { viendoComo, volverAMiCuenta } from '@/lib/ver-como';

/**
 * Fetch wrapper de Conversia.
 *
 * - Base URL configurable (`VITE_API_URL`); por defecto relativo, de modo que
 *   el proxy de Vite (`/api` → http://localhost:3000) funcione en dev.
 * - Inyecta `Authorization: Bearer <accessToken>` leyendo el auth store.
 * - Refresh transparente en 401: reintenta una vez tras renovar el token.
 *   Las renovaciones concurrentes comparten una única promesa (`refreshPromise`).
 * - Desempaqueta el envelope `{ data }` / `{ error }` y lanza `ApiError` tipado.
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/** Error de API con código, status HTTP y detalles opcionales. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Cuerpo JSON; se serializa automáticamente. */
  body?: unknown;
  /** Query params; se serializan ignorando valores `undefined`/`null`. */
  query?: object;
  /** Si es true, no adjunta el header Authorization (rutas públicas). */
  skipAuth?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(
    path.startsWith('http') ? path : `${BASE}${path}`,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
  );
  if (query) {
    for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }
  }
  return BASE || path.startsWith('http') ? url.toString() : url.pathname + url.search;
}

/** Renueva el access token usando el refresh token. Idempotente y compartida. */
async function refreshTokens(): Promise<boolean> {
  // «Ver como» no se renueva: al vencerse, vuelve a la cuenta de la administración.
  if (viendoComo()) {
    volverAMiCuenta();
    return false;
  }
  const { refreshToken, setSession, clearSession } = useAuthStore.getState();
  if (!refreshToken) return false;
  try {
    const res = await fetch(buildUrl('/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      clearSession();
      return false;
    }
    const json = (await res.json()) as { data: AuthResponse };
    setSession(json.data);
    return true;
  } catch {
    clearSession();
    return false;
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const json = text ? JSON.parse(text) : {};
  if (!res.ok || (json && 'error' in json)) {
    const err = (json?.error ?? {}) as Partial<ApiErrorShape>;
    throw new ApiError(
      err.message ?? res.statusText ?? 'Error de red',
      err.code ?? 'HTTP_ERROR',
      res.status,
      err.details,
    );
  }
  return (json?.data ?? json) as T;
}

/** Punto de entrada genérico. Las APIs por dominio se construyen encima. */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, skipAuth, headers, ...rest } = options;

  // FormData (p. ej. subida de foto): NO se serializa a JSON ni se fija
  // Content-Type — el navegador pone `multipart/form-data` con su boundary.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const doFetch = (): Promise<Response> => {
    const finalHeaders = new Headers(headers as HeadersInit);
    if (body !== undefined && !isFormData && !finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json');
    }
    if (!skipAuth) {
      const token = useAuthStore.getState().accessToken;
      if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
      // NEGOCIO ACTIVO. Se inyecta acá, UNA vez, y por eso ninguna llamada de
      // dominio tiene que acordarse de mandarlo. Si todavía no hay lista
      // cargada, no se manda: el servidor resuelve solo cuando el usuario
      // tiene un único negocio (instancia de cliente) y responde 403 pidiendo
      // que elija cuando tiene varios. Ver middleware/workspace.middleware.ts.
      const workspaceId = getActiveWorkspaceId();
      if (workspaceId) finalHeaders.set('X-Workspace-Id', workspaceId);
    }
    return fetch(buildUrl(path, query), {
      ...rest,
      headers: finalHeaders,
      body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
    });
  };

  let res = await doFetch();

  if (res.status === 401 && !skipAuth && !path.includes('/auth/')) {
    refreshPromise = refreshPromise ?? refreshTokens();
    const ok = await refreshPromise;
    refreshPromise = null;
    if (ok) res = await doFetch();
  }

  return parse<T>(res);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  del: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
